import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import React, { Component } from 'react'

const PLACEHOLDER_STYLE = {
  height: 140,
  marginBottom: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f0f2f5',
  border: '1px dashed #c7cdd4',
  borderRadius: 4,
  color: '#8a939c',
}

const CARD_STYLE = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}

export default class MessageListTemplates extends Component {
  constructor(props) {
    super(props)
    this.state = {
      templates: [],
      loading: true,
      loadError: null,
      renameTarget: null,
      renameValue: '',
      deleteTarget: null,
      busy: false,
      actionError: null,
    }
  }

  componentDidMount() {
    this.loadTemplates()
  }

  loadTemplates = () => {
    this.setState({ loading: true, loadError: null })
    this.props.firebase
      .getMessageListMessageTemplates()
      .then((templates) => {
        this.setState({ templates: templates || [], loading: false })
      })
      .catch((err) => {
        console.error('Error loading message templates', err)
        this.setState({ templates: [], loading: false, loadError: 'Mallipohjien lataus epäonnistui.' })
      })
  }

  describeError = (err) =>
    err && err.code === 'permission-denied'
      ? 'Ei käyttöoikeutta mallipohjien muokkaamiseen. Ota yhteyttä tukeen.'
      : 'Toiminto epäonnistui. Yritä uudelleen.'

  handleSelectTemplate = (template) => {
    if (this.props.onSelectTemplate) {
      this.props.onSelectTemplate(template)
    }
  }

  openRename = (template) =>
    this.setState({ renameTarget: template, renameValue: template.name || '', actionError: null })

  closeRename = () => this.setState({ renameTarget: null, renameValue: '', actionError: null })

  confirmRename = () => {
    const { renameTarget, renameValue } = this.state
    const name = (renameValue || '').trim()
    if (!renameTarget || !name) return
    this.setState({ busy: true, actionError: null })
    this.props.firebase
      .updateMessageListMessageTemplate({ id: renameTarget.id, name })
      .then(() => {
        this.setState({ busy: false, renameTarget: null, renameValue: '' }, this.loadTemplates)
      })
      .catch((err) => {
        console.error('Error renaming message template', err)
        this.setState({ busy: false, actionError: this.describeError(err) })
      })
  }

  openDelete = (template) => this.setState({ deleteTarget: template, actionError: null })

  closeDelete = () => this.setState({ deleteTarget: null, actionError: null })

  confirmDelete = () => {
    const { deleteTarget } = this.state
    if (!deleteTarget) return
    this.setState({ busy: true, actionError: null })
    this.props.firebase
      .deleteMessageListMessageTemplate(deleteTarget.id)
      .then(() => {
        this.setState({ busy: false, deleteTarget: null }, this.loadTemplates)
      })
      .catch((err) => {
        console.error('Error deleting message template', err)
        this.setState({ busy: false, actionError: this.describeError(err) })
      })
  }

  renderCard(template) {
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
        <Card style={CARD_STYLE}>
          <CardContent>
            {template.thumbnail ? (
              <CardMedia
                component='img'
                image={template.thumbnail}
                alt={template.name || ''}
                style={{ height: 140, objectFit: 'contain', marginBottom: 10 }}
              />
            ) : (
              <div style={PLACEHOLDER_STYLE}>
                <Typography variant='caption'>Ei esikatselukuvaa</Typography>
              </div>
            )}
            <Typography variant='h6'>{template.name || '(Nimetön)'}</Typography>
            {template.subject ? (
              <Typography variant='caption' color='text.secondary'>
                Otsikko: {template.subject}
              </Typography>
            ) : null}
          </CardContent>
          <CardActions>
            <Button size='small' color='primary' onClick={() => this.handleSelectTemplate(template)}>
              Valitse
            </Button>
            {/* System templates live in the shared root collection and stay read only. */}
            {!template.isSystem && (
              <React.Fragment>
                <Button size='small' onClick={() => this.openRename(template)}>
                  Nimeä uudelleen
                </Button>
                <Button size='small' color='error' onClick={() => this.openDelete(template)}>
                  Poista
                </Button>
              </React.Fragment>
            )}
          </CardActions>
        </Card>
      </Grid>
    )
  }

  renderSection(title, templates, emptyText) {
    return (
      <div style={{ marginBottom: 30 }}>
        <Typography variant='h5' gutterBottom>
          {title}
        </Typography>
        {templates.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {emptyText}
          </Typography>
        ) : (
          <Grid container spacing={2} alignItems='stretch'>
            {templates.map((t) => this.renderCard(t))}
          </Grid>
        )}
      </div>
    )
  }

  render() {
    const { templates, loading, loadError } = this.state
    const systemTemplates = templates.filter((t) => t.isSystem)
    const ownTemplates = templates
      .filter((t) => !t.isSystem)
      .sort(
        (a, b) =>
          ((b.created && b.created.seconds) || 0) - ((a.created && a.created.seconds) || 0) ||
          (a.name || '').localeCompare(b.name || '')
      )

    if (loading) {
      return (
        <div style={{ padding: 20 }}>
          <CircularProgress color='primary' size={24} /> <span>Ladataan mallipohjia...</span>
        </div>
      )
    }

    return (
      <div style={{ padding: 20 }}>
        {loadError && (
          <Typography variant='body2' color='error' gutterBottom>
            {loadError}
          </Typography>
        )}

        {this.renderSection('Valmiit pohjat', systemTemplates, 'Ei valmiita pohjia.')}
        {this.renderSection(
          'Omat pohjat',
          ownTemplates,
          'Ei omia pohjia. Voit tallentaa muokkaamasi viestin mallipohjaksi Viesti-välilehdellä.'
        )}

        <Dialog open={!!this.state.renameTarget} aria-labelledby='rename-template-title'>
          <div style={{ padding: 20, minWidth: 380 }}>
            <Typography variant='h6' id='rename-template-title' gutterBottom>
              Nimeä mallipohja uudelleen
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size='small'
              variant='outlined'
              label='Mallipohjan nimi'
              value={this.state.renameValue}
              onChange={(e) => this.setState({ renameValue: e.target.value })}
            />
            {this.state.actionError && (
              <Typography variant='body2' color='error' style={{ marginTop: 10 }}>
                {this.state.actionError}
              </Typography>
            )}
            <div style={{ textAlign: 'right', marginTop: 20 }}>
              <Button onClick={this.closeRename} disabled={this.state.busy}>
                Peruuta
              </Button>
              <Button
                color='primary'
                onClick={this.confirmRename}
                disabled={this.state.busy || !(this.state.renameValue || '').trim()}
              >
                {this.state.busy ? 'Tallennetaan...' : 'Tallenna'}
              </Button>
            </div>
          </div>
        </Dialog>

        <Dialog open={!!this.state.deleteTarget} aria-labelledby='delete-template-title'>
          <div style={{ padding: 20, minWidth: 380 }}>
            <Typography variant='h6' id='delete-template-title' gutterBottom>
              Poista mallipohja
            </Typography>
            <Typography variant='body1' gutterBottom>
              Haluatko varmasti poistaa mallipohjan "
              {this.state.deleteTarget ? this.state.deleteTarget.name : ''}"?
            </Typography>
            {this.state.actionError && (
              <Typography variant='body2' color='error' style={{ marginTop: 10 }}>
                {this.state.actionError}
              </Typography>
            )}
            <div style={{ textAlign: 'right', marginTop: 20 }}>
              <Button onClick={this.closeDelete} disabled={this.state.busy}>
                Peruuta
              </Button>
              <Button color='error' onClick={this.confirmDelete} disabled={this.state.busy}>
                {this.state.busy ? 'Poistetaan...' : 'Poista'}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    )
  }
}
